export const queryKeys = {
  dashboardStats: (
    range: string,
    includeSynthetic: boolean,
    from?: string,
    to?: string,
    compareFrom?: string,
    compareTo?: string,
  ) =>
    [
      'dashboard',
      'stats',
      range,
      includeSynthetic,
      from ?? null,
      to ?? null,
      compareFrom ?? null,
      compareTo ?? null,
    ] as const,
  followerLeaderboard: (
    metric: string,
    range: string,
    includeSynthetic: boolean,
    from?: string,
    to?: string,
  ) =>
    [
      'dashboard',
      'leaderboard',
      'followers',
      metric,
      range,
      includeSynthetic,
      from ?? null,
      to ?? null,
    ] as const,
  monthlyReport: (params: Record<string, unknown>) => ['monthlyReport', params] as const,
  users: (filters?: Record<string, unknown>) => ['users', filters ?? {}] as const,
  user: (id: string) => ['users', id] as const,
  // Filter-scoped, so the key includes the same filters as `users` above —
  // a filter change must refetch the stats, not just the table.
  userStats: (filters?: Record<string, unknown>) => ['users', 'stats', filters ?? {}] as const,
  userProfileStats: (filters?: Record<string, unknown>) => ['users', 'profile-stats', filters ?? {}] as const,
  userRetentionStats: (filters?: Record<string, unknown>) => ['users', 'retention-stats', filters ?? {}] as const,
  userCountryOptions: () => ['users', 'country-options'] as const,
  reports: (status?: string) => ['reports', status ?? 'all'] as const,
  reportInsights: (range: string, from?: string, to?: string) =>
    ['admin-reports', 'insights', range, from ?? null, to ?? null] as const,
  reportTable: (range: string, from?: string, to?: string) =>
    ['admin-reports', 'table', range, from ?? null, to ?? null] as const,
  vendors: () => ['vendors'] as const,
  vendor: (id: string) => ['vendors', id] as const,
  onboardingScreens: () => ['onboarding', 'screens'] as const,
  onboardingQuestions: () => ['onboarding', 'questions'] as const,
  settings: () => ['settings'] as const,
  supplierCategories: () => ['supplierCategories'] as const,
  supplierPlans: () => ['supplierPlans'] as const,
  suppliers: (filters?: Record<string, unknown>) => ['suppliers', filters ?? {}] as const,
  supplier: (id: string) => ['suppliers', id] as const,
  supplierProducts: (supplierId: string, cursor?: string) =>
    ['supplierProducts', supplierId, cursor ?? null] as const,
  supplierProduct: (id: string) => ['supplierProducts', 'item', id] as const,
  affiliateCodes: (filters?: Record<string, unknown>) => ['affiliateCodes', filters ?? {}] as const,
  admins: () => ['admins'] as const,
  adminDevices: (adminId: string) => ['admins', adminId, 'mfa', 'devices'] as const,
  supplierAdminStats: () => ['suppliers', 'admin', 'stats'] as const,
  supplierAdminStatsWindowed: (range: string, from?: string, to?: string) =>
    ['suppliers', 'admin', 'stats', 'windowed', range, from ?? null, to ?? null] as const,
  supplierInsights: (id: string, range: string, from?: string, to?: string) =>
    ['suppliers', 'admin', id, 'insights', range, from ?? null, to ?? null] as const,
  jobInsights: (range: string, from?: string, to?: string) =>
    ['admin-jobs', 'insights', range, from ?? null, to ?? null] as const,
  syntheticBotInsights: (range: string, from?: string, to?: string) =>
    ['synthetic-bots', 'insights', range, from ?? null, to ?? null] as const,
  productEnquiriesAdmin: (cursor?: string) => ['productEnquiries', 'admin', cursor ?? null] as const,
  oneMallInsights: (range: string, from?: string, to?: string, currency?: string) =>
    ['onemall', 'insights', range, from ?? null, to ?? null, currency ?? null] as const,
  oneMallProducts: (params: Record<string, unknown>) => ['onemall', 'products', params] as const,
  oneMallProduct: (id: string) => ['onemall', 'product', id] as const,
  oneMallSellers: (params: Record<string, unknown>) => ['onemall', 'sellers', params] as const,
  oneMallSeller: (userId: string) => ['onemall', 'seller', userId] as const,
  oneMallEnquiries: (params: Record<string, unknown>) => ['onemall', 'enquiries', params] as const,
  oneMallShops: (params: Record<string, unknown>) => ['onemall', 'shops', params] as const,
  trendingHashtags: () => ['hashtags', 'trending'] as const,
  hashtagInsights: (params: object) => ['hashtags', 'insights', params] as const,
  hashtagTable: (params: object) => ['hashtags', 'table', params] as const,
  callsInsights: (range: string) => ['calls', 'insights', range] as const,
  liveInsights: (range: string) => ['live', 'insights', range] as const,
  activeLives: () => ['live', 'active'] as const,
  walletInsights: () => ['admin-wallets', 'insights'] as const,
  mfaStatus: () => ['admin', 'mfa', 'status'] as const,
  adminPermissions: (adminId: string, apiBaseUrl: string) =>
    ['admin', 'permissions', adminId, apiBaseUrl] as const,
  creativeCreators: (filters?: Record<string, unknown>) =>
    ['creative', 'creators', filters ?? {}] as const,
  creativeVideos: (filters?: Record<string, unknown>) =>
    ['creative', 'videos', 'list', filters ?? {}] as const,
  creativeVideo: (id: string) => ['creative', 'videos', 'detail', id] as const,
  creativePipeline: () => ['creative', 'pipeline'] as const,
  creativeAnalyticsOverview: (days: number) => ['creative', 'analytics', 'overview', days] as const,
  creativeAnalyticsVideos: (days: number) => ['creative', 'analytics', 'videos', days] as const,
  creativeAnalyticsCreators: (days: number) => ['creative', 'analytics', 'creators', days] as const,
  creativeAnalyticsTags: (days: number) => ['creative', 'analytics', 'tags', days] as const,
  creativeAnalyticsGenres: (days: number) => ['creative', 'analytics', 'genres', days] as const,
  // High Ticket Ad Space. Everything starts with 'advertising', so one
  // invalidation after an action refreshes every page that may show it.
  advertising: () => ['advertising'] as const,
  adConfig: () => ['advertising', 'config'] as const,
  adBookings: (params: Record<string, unknown>) => ['advertising', 'bookings', params] as const,
  adBooking: (id: string) => ['advertising', 'booking', id] as const,
  adCalendar: (from: string, weeks: number) => ['advertising', 'calendar', from, weeks] as const,
  adCreatives: (params: Record<string, unknown>) => ['advertising', 'creatives', params] as const,
  adMetrics: (bookingId: string) => ['advertising', 'metrics', bookingId] as const,
  adAdvertisers: (params: Record<string, unknown>) => ['advertising', 'advertisers', params] as const,
  adAdvertiser: (id: string) => ['advertising', 'advertiser', id] as const,
  adReports: (params: Record<string, unknown>) => ['advertising', 'reports', params] as const,
}
